/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from "rxjs";

// PrimeNG imports
import { ButtonModule } from "primeng/button";
import { CheckboxModule } from "primeng/checkbox";
import { DatePickerModule } from "primeng/datepicker";
import { DividerModule } from "primeng/divider";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { InputTextModule } from "primeng/inputtext";

// Our interfaces
import {
  CustomerFilter,
  DEFAULT_CUSTOMER_FILTER_VALUES,
  ICustomerFilterControls,
} from "../../interfaces/customer-filter.interface";

/**
 * Customers Filters Component
 * Handles filtering logic matching old QMC dashboard-customers.component.html:
 * - Search
 * - Start Date / End Date (customerCreatedRange)
 * - Filter by Deleted (includeDeleted)
 */
@Component({
  selector: "app-customers-filters",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    CheckboxModule,
    DatePickerModule,
    DividerModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
  ],
  templateUrl: "./customers-filters.component.html",
  styleUrl: "./customers-filters.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomersFiltersComponent implements OnInit, OnDestroy {
  // Input/Output pattern
  @Input() filterForm!: FormGroup<ICustomerFilterControls>;
  @Input() loading = false;
  @Input() drawerMode = false;
  @Input() skipLocalStorageLoad = false; // Skip loading from localStorage (used when parent provides filters via query params)

  @Output() filtersChanged = new EventEmitter<CustomerFilter>();
  @Output() clearFilters = new EventEmitter<void>();

  // Component state
  private readonly destroy$ = new Subject<void>();

  // Date constraints
  maxDate = new Date();

  /**
   * Check if any filters are active
   * Matches old QMC filters: search, date range, isDeleted
   */
  hasActiveFilters(): boolean {
    if (!this.filterForm) return false;

    const formValue = this.filterForm.value;
    return !!(
      formValue.search?.trim() ||
      formValue.includeDeleted ||
      (Array.isArray(formValue.customerCreatedRange) &&
        formValue.customerCreatedRange.length > 0)
    );
  }

  ngOnInit(): void {
    // Only load from localStorage if skipLocalStorageLoad is false
    // When navigating from command palette with search query param, parent sets the form values
    // and we should NOT override them with localStorage values
    if (!this.skipLocalStorageLoad) {
      this.loadPersistedFilters();
    }
    this.setupFormSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Setup form subscriptions for real-time filtering
   * Matches old QMC filters: search, date range, isDeleted
   */
  private setupFormSubscriptions(): void {
    if (!this.filterForm) return;

    // Debounce search input
    this.filterForm.controls.search.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.emitFilterChange();
      });

    // Include deleted checkbox
    this.filterForm.controls.includeDeleted.valueChanges
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => {
        this.emitFilterChange();
        this.persistFilters();
      });

    // Date range - only emit when complete range selected
    this.filterForm.controls.customerCreatedRange.valueChanges
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe((rangeValue) => {
        if (rangeValue === null || rangeValue === undefined) {
          this.emitFilterChange();
          this.persistFilters();
        } else if (
          Array.isArray(rangeValue) &&
          rangeValue.length === 2 &&
          rangeValue[0] &&
          rangeValue[1]
        ) {
          this.emitFilterChange();
          this.persistFilters();
        }
      });
  }

  /**
   * Emit filter changes to parent component
   * Matches old QMC filters: search, date range, isDeleted
   */
  private emitFilterChange(): void {
    if (!this.filterForm) return;

    const formValue = this.filterForm.value;
    const filter: CustomerFilter = {
      siteId: formValue.siteId!,
      search: formValue.search?.trim() || undefined,
      isDeleted: formValue.includeDeleted || undefined,
      pageSize: formValue.pageSize!,
      page: formValue.page!,
      orderBy: formValue.sortBy || undefined,
      order: formValue.sortDirection === "desc" ? "desc" : "asc",

      // Date range
      ...this.extractDateRangeFilters(formValue),
    };

    this.filtersChanged.emit(filter);
  }

  /**
   * Extract date range filters from form values
   */
  private extractDateRangeFilters(
    formValue: Partial<{
      customerCreatedRange: Date[] | null;
    }>,
  ): Partial<CustomerFilter> {
    const dateFilters: Partial<CustomerFilter> = {};

    const range = formValue.customerCreatedRange;
    if (Array.isArray(range) && range.length === 2 && range[0] && range[1]) {
      const normalizedStart = new Date(range[0]);
      normalizedStart.setHours(0, 0, 0, 0);

      const normalizedEnd = new Date(range[1]);
      normalizedEnd.setHours(23, 59, 59, 999);

      dateFilters.customerCreatedFromDate = normalizedStart;
      dateFilters.customerCreatedToDate = normalizedEnd;
    }

    return dateFilters;
  }

  /**
   * Clear all filters
   * Matches old QMC filters: search, date range, isDeleted
   */
  onClearFilters(): void {
    if (!this.filterForm) return;

    this.filterForm.patchValue({
      search: null,
      customerCreatedRange: null,
      includeDeleted: false,
      page: DEFAULT_CUSTOMER_FILTER_VALUES.page,
      pageSize: DEFAULT_CUSTOMER_FILTER_VALUES.pageSize,
      sortBy: DEFAULT_CUSTOMER_FILTER_VALUES.sortBy,
      sortDirection: DEFAULT_CUSTOMER_FILTER_VALUES.sortDirection,
    });

    this.clearPersistedFilters();
    this.clearFilters.emit();
  }

  /**
   * Get CSS class for search section based on mode
   */
  getSearchSectionClass(): string {
    if (this.drawerMode) {
      return "p-3";
    }
    return "mb-4";
  }

  /**
   * Persist filters to localStorage
   * Matches old QMC filters: search, date range, isDeleted
   */
  private persistFilters(): void {
    if (!this.filterForm) return;

    const filtersToSave = {
      search: this.filterForm.controls.search.value,
      includeDeleted: this.filterForm.controls.includeDeleted.value,
      customerCreatedRange: this.serializeDateRange(
        this.filterForm.controls.customerCreatedRange.value,
      ),
    };

    localStorage.setItem("customers.filters", JSON.stringify(filtersToSave));
  }

  /**
   * Load persisted filters from localStorage
   * Matches old QMC filters: search, date range, isDeleted
   */
  private loadPersistedFilters(): void {
    if (!this.filterForm) return;

    try {
      const saved = localStorage.getItem("customers.filters");
      if (saved) {
        const filters = JSON.parse(saved);

        this.filterForm.patchValue(
          {
            search: filters.search,
            includeDeleted: filters.includeDeleted || false,
            customerCreatedRange: this.deserializeDateRange(
              filters.customerCreatedRange,
            ),
          },
          { emitEvent: false },
        );
      }
    } catch (error) {
      console.warn("Failed to load persisted filters:", error);
      this.clearPersistedFilters();
    }
  }

  /**
   * Clear persisted filters from localStorage
   */
  private clearPersistedFilters(): void {
    localStorage.removeItem("customers.filters");
  }

  /**
   * Serialize date range for localStorage
   */
  private serializeDateRange(range: Date[] | null): {
    startDate: string | null;
    endDate: string | null;
  } | null {
    if (!range || !Array.isArray(range) || range.length === 0 || !range[0]) {
      return null;
    }
    return {
      startDate: range[0] ? range[0].toISOString() : null,
      endDate: range[1] ? range[1].toISOString() : null,
    };
  }

  /**
   * Deserialize date range from localStorage
   */
  private deserializeDateRange(
    range: {
      startDate: string | null;
      endDate: string | null;
    } | null,
  ): Date[] | null {
    if (!range || !range.startDate) return null;

    const dates: Date[] = [new Date(range.startDate)];
    if (range.endDate) {
      dates.push(new Date(range.endDate));
    }
    return dates;
  }
}
